import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './AdminActivityLogs.css';

const AdminActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({});
  const [availableFilters, setAvailableFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState({});

  // Default filters
  const [filterState, setFilterState] = useState({
    action: '',
    admin_user: '',
    target_type: '',
    days: 30,
    limit: 100
  });

  useEffect(() => {
    fetchLogs();
  }, [filterState]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAdminActivityLogs(filterState);
      
      setLogs(response.logs);
      setAvailableFilters(response.filters);
      setQuery(response.query);
      setError(null);
    } catch (err) {
      setError('Failed to fetch activity logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilterState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilterState({
      action: '',
      admin_user: '',
      target_type: '',
      days: 30,
      limit: 100
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDetails = (details) => {
    if (!details) return 'No details';
    try {
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return details;
    }
  };

  const getActionColor = (action) => {
    if (action.includes('DELETE')) return '#dc3545';
    if (action.includes('CREATE')) return '#28a745';
    if (action.includes('UPDATE')) return '#ffc107';
    return '#6c757d';
  };

  if (loading) {
    return (
      <div className="admin-logs-container">
        <div className="loading">Loading admin activity logs...</div>
      </div>
    );
  }

  return (
    <div className="admin-logs-container">
      <div className="admin-logs-header">
        <h1>🔍 Admin Activity Logs</h1>
        <p className="subtitle">Secret page for monitoring admin actions</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Action:</label>
            <select 
              value={filterState.action} 
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All Actions</option>
              {availableFilters.actions?.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Admin User:</label>
            <select 
              value={filterState.admin_user} 
              onChange={(e) => handleFilterChange('admin_user', e.target.value)}
            >
              <option value="">All Users</option>
              {availableFilters.adminUsers?.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Target Type:</label>
            <select 
              value={filterState.target_type} 
              onChange={(e) => handleFilterChange('target_type', e.target.value)}
            >
              <option value="">All Types</option>
              {availableFilters.targetTypes?.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Time Period:</label>
            <select 
              value={filterState.days} 
              onChange={(e) => handleFilterChange('days', parseInt(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Limit:</label>
            <select 
              value={filterState.limit} 
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            >
              <option value={50}>50 records</option>
              <option value={100}>100 records</option>
              <option value={200}>200 records</option>
              <option value={500}>500 records</option>
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearFilters} className="btn btn-secondary">
            Clear Filters
          </button>
          <button onClick={fetchLogs} className="btn btn-primary">
            Refresh
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <span>Showing {logs.length} logs</span>
        {query.action && <span className="filter-tag">Action: {query.action}</span>}
        {query.admin_user && <span className="filter-tag">User: {query.admin_user}</span>}
        {query.target_type && <span className="filter-tag">Type: {query.target_type}</span>}
        <span className="filter-tag">Period: {query.days} days</span>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Logs Table */}
      <div className="logs-table-container">
        {logs.length === 0 ? (
          <div className="no-logs">
            <p>No activity logs found for the selected filters.</p>
            <p>Try adjusting your filters or check if the logging system is working.</p>
          </div>
        ) : (
          <table className="logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Admin</th>
                <th>Action</th>
                <th>Target</th>
                <th>Description</th>
                <th>IP Address</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="log-row">
                  <td className="timestamp">
                    {formatDate(log.created_at)}
                  </td>
                  <td className="admin-user">
                    <strong>{log.admin_username || 'Unknown'}</strong>
                  </td>
                  <td className="action">
                    <span 
                      className="action-badge"
                      style={{ backgroundColor: getActionColor(log.action) }}
                    >
                      {log.action}
                    </span>
                  </td>
                  <td className="target">
                    {log.target_type && (
                      <span className="target-type">{log.target_type}</span>
                    )}
                    {log.target_id && (
                      <span className="target-id">#{log.target_id}</span>
                    )}
                  </td>
                  <td className="description">
                    {log.target_description || 'No description'}
                  </td>
                  <td className="ip-address">
                    {log.ip_address || 'Unknown'}
                  </td>
                  <td className="details">
                    <details>
                      <summary>View Details</summary>
                      <pre>{formatDetails(log.details)}</pre>
                      {log.user_agent && (
                        <div className="user-agent">
                          <strong>User Agent:</strong> {log.user_agent}
                        </div>
                      )}
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminActivityLogs;
